import React, { FC, useEffect, useReducer } from 'react';

import { DataTypeForES } from '@ariksa/reporting';
import {
  Box,
  Center,
  Divider,
  HStack,
  Stack,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { each, map, orderBy, sortBy, sum, values } from 'lodash';
import round from 'lodash/round';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';
import { colorOpacity } from 'theme/utils';

import {
  formatNumberWithComma,
  formatTooltip,
  getIcon,
  getProviderName,
  PageHeaderWithIcon,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import {
  defaultSelectStyles,
  RadioButtonGroup,
  Select,
} from 'components/DataEntry';
import { OutlinedMenuButton } from 'components/DataEntry/Button/OutlinedMenuButton';
import {
  ExportIcon,
  GraphDecreaseIcon,
  GraphIncreaseIcon,
  InformationIcon,
  MinusIcon,
  PieChartIcon,
} from 'components/Icons';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { DashboardOverviewCard } from 'containers/Dashboard/utils/DashboardOverviewCard';
import { RegionAndVPCFilter } from 'containers/Inventory/CloudInventory/Components/Overview/RegionAndVPCFilter';
import {
  cloudProviderOptions,
  groupByOptions,
  sortByOptions,
} from 'containers/Inventory/CloudInventory/Components/Overview/utils';
import { useInventoryContext } from 'containers/Inventory/CloudInventory/hooks/context';
import { useInventory } from 'containers/Inventory/CloudInventory/hooks/useInventory';
import { selectInventory } from 'containers/Inventory/CloudInventory/selector';
import { actions } from 'containers/Inventory/CloudInventory/slice';
import { ExportAlertsModal } from 'containers/SharedState/Components/ExportAlerts/ExportAlertsModal';
import { selectSharedState } from 'containers/SharedState/selectors';

interface Props {}

export const Overview: FC<Props> = props => {
  const { inventoryOverview, inventoryHistory } = useSelector(selectInventory);
  const { searchTerm } = useSelector(selectSharedState);
  const {
    setShowInventorySummary,
    sideNavItemsMapping,
    selectedRegion,
    selectedVpc,
    useInventoryV3,
  } = useInventoryContext();
  const {
    getResourceTypeOptionsWithAlias,
    getCloudNativeName,
    getResourceAlias,
  } = useResourceType();
  const dispatch = useDispatch();
  const { environmentId, accountId } = useAccessBoundary();
  const { environmentMapping } = useEnvironmentOptions();
  const { getVpcs } = useInventory();
  const exportAlerts = useDisclosure();
  const { updateValuesInURL } = useSearchParamFunctions();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      resourceType: { label: 'Resource type', value: undefined },
      resourceTypeOptions: [],
      filteredResourceTypeOptions: [],
      groupBy: groupByOptions[0],
      cloud: cloudProviderOptions[0],
      categories: {},
      sortBy: sortByOptions[1]?.value,
      mouseOverItem: undefined,
      isFlipped: false,
      currentKey: '',
    },
  );

  useEffect(() => {
    getVpcs();
  }, [getVpcs]);

  useEffect(() => {
    if (!environmentId && !accountId) return;
    dispatch(
      actions.getInventoryOverview({
        q: {
          environmentId,
          accountId: !accountId ? undefined : [accountId],
          region: selectedRegion?.value,
          nativeResource: state.resourceType?.value,
          searchTerm: searchTerm ?? undefined,
          vpc: selectedVpc?.value ? [selectedVpc?.value] : undefined,
          useInventoryV3,
        },
      } as any),
    );
    dispatch(
      actions.getInventoryHistory({
        q: {
          environmentId: environmentId!,
          accountId: !!accountId ? [accountId] : undefined,
          startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
          endDate: dayjs().format('YYYY-MM-DD'),
          useInventoryV3,
        },
      } as any),
    );
  }, [
    dispatch,
    environmentId,
    accountId,
    selectedRegion,
    selectedVpc,
    state.resourceType,
    searchTerm,
    useInventoryV3,
  ]);

  //set resource type options
  useEffect(() => {
    const options = [
      { label: 'Resource type', value: undefined },
      ...getResourceTypeOptionsWithAlias(),
    ];
    updateState({
      resourceTypeOptions: options,
      filteredResourceTypeOptions: options,
    });
  }, [getResourceTypeOptionsWithAlias]);

  useEffect(() => {
    //sort all the resources
    // Count sort is now largest first (was ascending); alphabetical stays A-Z.
    let sortedResources =
      state.sortBy === 'Count'
        ? orderBy(inventoryOverview.data, ['total_resources'], ['desc'])
        : sortBy(inventoryOverview.data, ['resource_type']);

    //create category: resources mapping
    let categories: Record<string, any> = {};
    if (!state.groupBy?.value) {
      // Dropped a dead total_entities write; the total is computed per category below.
      categories['All'] = sortedResources;
    } else {
      each(sortedResources, o => {
        if (!!categories[o[state.groupBy?.value]])
          categories[o[state.groupBy?.value]].push(o);
        else {
          categories[o[state.groupBy?.value]] = [o];
        }
      });
    }

    //store all resources under each category
    let items: Record<string, any> = {};
    each(categories, (o, key) => {
      items[key] = {
        resources: o,
        key,
        iconType: key === 'All' ? 'Environment' : key,
        total_entities: sum(map(o, r => r.total_resources)),
      };
    });

    //sort each category
    let sortedCategories = sortBy(values(items), ['key']);

    //filter by cloud
    if (!!state.cloud?.value) {
      sortedCategories = !!items[state.cloud?.value]
        ? [items[state.cloud?.value]]
        : [];
    }

    updateState({ categories: sortedCategories });
  }, [inventoryOverview.data, state.groupBy, state.sortBy, state.cloud]);

  const styles = {
    menu: provided => ({
      ...provided,
      width: 'max-content',
      minWidth: '100%',
      //right: 0,
    }),
    valueContainer: (styles, provided) => ({
      ...defaultSelectStyles().valueContainer(styles, provided),
    }),
  };

  const renderChangeIcon = change => (
    <Center w="full" h="full">
      {change === '-' || change === 0 ? (
        <Center boxSize={2}>
          <MinusIcon />
        </Center>
      ) : change > 0 ? (
        <GraphIncreaseIcon color="red.300" />
      ) : (
        <GraphDecreaseIcon color="green.300" />
      )}
    </Center>
  );

  const renderSmallCard = item => {
    const key = item.native_resource + '_' + item.category;
    const resourceCount =
      inventoryHistory.data?.[item.native_resource]?.resource_count;
    let percentChange: number | string = '-';
    if (!!resourceCount)
      percentChange =
        resourceCount?.percentage_change === '-'
          ? '-'
          : round(resourceCount?.percentage_change, 0);
    return (
      <CustomTooltip
        label={formatTooltip({
          header: getResourceAlias(item.native_resource),
          content: (
            <Stack spacing={0} w="full">
              <Box>Category: {item.category}</Box>
              <Box>
                Cloud: {getProviderName(item.cloud_provider?.toLowerCase())}
              </Box>
              <Box>
                Cloud Native: {getCloudNativeName(item.native_resource)}
              </Box>
              <Box>
                Business Critical:{' '}
                {environmentMapping?.[environmentId!]?.is_business_critical
                  ? 'Yes'
                  : 'No'}
              </Box>
              <Box>
                Change:{' '}
                {/* 0% now reads "No change", matching the minus icon. */}
                {percentChange === '-' || percentChange === 0
                  ? 'No change'
                  : percentChange +
                    '% ' +
                    (percentChange > 0 ? 'increase' : 'decrease')}{' '}
                in last 7 days
              </Box>
            </Stack>
          ),
          width: 64,
        })}
        placement="right"
      >
        <Box
          border="1px solid"
          borderRadius={6}
          borderColor={customTheme.colors.gray[100]}
          w="125px"
          py={2}
          px={2}
          bg="white"
          zIndex={800}
          h="130px"
        >
          <Stack justify="space-between" h="full">
            <HStack justify="space-between">
              {/*<Box>
                <Center boxSize={4} color={getIconColor(item.native_resource)}>
                  {getIcon(item.native_resource)}
                </Center>
              </Box>*/}
              <Box>
                <Center boxSize={4} pt={item.cloud_provider === 'AWS' ? 1 : 0}>
                  {getIcon(item.cloud_provider)}
                </Center>
              </Box>
              <Box
                onClick={e => {
                  e.stopPropagation();
                  handleClick(key);
                }}
              >
                <Center boxSize={4}>
                  <InformationIcon
                    color={colorOpacity(customTheme.colors.primary, 0.3)}
                  />
                </Center>
              </Box>
            </HStack>
            <Box w="full">
              <Center fontWeight={600} textAlign="center">
                <Text noOfLines={2} textOverflow="ellipsis">
                  {getResourceAlias(item.native_resource)}
                </Text>
              </Center>
            </Box>
            <HStack justify="center">
              <Center fontSize="lg" w="full">
                {formatNumber(item.total_resources, 1)}
              </Center>
              <Box boxSize={4} position="absolute" right={2}>
                {inventoryHistory.isLoading ? (
                  <CustomSpinner />
                ) : (
                  renderChangeIcon(percentChange)
                )}
              </Box>
            </HStack>
          </Stack>
        </Box>
      </CustomTooltip>
    );
  };

  // Removed an unused percentChange calculation (only the commented-out tooltip read it).
  const renderDetailedCard = item => {
    return (
      <Box
        position="absolute"
        border="1px solid"
        borderRadius={6}
        borderColor="primary"
        w={48}
        py={2}
        px={2}
        bg="white"
        zIndex={900}
        h={48}
      >
        <Stack justify="space-between" h="full">
          <HStack w="full" justify="center">
            <Box boxSize={6} color="primary">
              <Center>{getIcon(item.native_resource)}</Center>
            </Box>
            {/*<CustomTooltip
              label={
                percentChange === 0 || percentChange === '-'
                  ? 0
                  : percentChange + '% over last 7 days'
              }
              w={24}
              textAlign="center"
            >
              <HStack
                spacing={0}
                fontSize="11px"
                color={
                  percentChange === 0 || percentChange === '-'
                    ? 'primary'
                    : percentChange > 0
                    ? 'red.300'
                    : 'green.300'
                }
              >
                <Box>
                  {percentChange === 0 || percentChange === '-'
                    ? 0
                    : percentChange > 0
                    ? '+' + percentChange
                    : percentChange}
                  %
                </Box>
                <Box boxSize={4}>{renderChangeIcon(percentChange)}</Box>
              </HStack>
            </CustomTooltip>*/}
          </HStack>
          <Center fontWeight={600}>{item.total_resources}</Center>
          <Center fontWeight={600} textAlign="center">
            {getCloudNativeName(item.native_resource)}
          </Center>
          <Stack spacing={0}>
            <Center>
              {item?.accounts?.length ?? 0} account
              {item?.accounts?.length === 1 ? '' : 's'}
            </Center>
            <Center>
              {item?.regions?.length ?? 0} region
              {item?.regions?.length === 1 ? '' : 's'}
            </Center>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const variants = {
    visible: { transform: 'rotateY(180deg)' },
    hidden: { transform: 'rotateY(0deg)' },
  };

  const variants2 = {
    visible: { transform: 'rotateY(-180deg)' },
    hidden: { transform: 'rotateY(0deg)' },
  };

  const handleClick = (key?: string) => {
    updateState({ isFlipped: !state.isFlipped, currentKey: key });
  };

  const renderCategoryCount = item => {
    const key = item.native_resource + '_' + item.category;
    return (
      <Box
        position="relative"
        style={{ transformStyle: 'preserve-3d' }}
        h="full"
        cursor="pointer"
        onClick={() => {
          updateValuesInURL({
            resource_category: sideNavItemsMapping[item.category]
              ?.key as string,
            resource_type: item.native_resource,
          });
          /*const menuItem = sideNavItemsMapping[item.category];
          dispatch(actions.setNativeResource(item.native_resource));
          dispatch(actions.updateCurrentCategory(item.category as string));
          dispatch(actions.updateCurrentCategoryTitle(menuItem?.data?.name));*/
        }}
        zIndex={state.currentKey !== key ? 800 : 900}
      >
        <motion.div
          variants={variants}
          animate={state.currentKey !== key ? 'hidden' : 'visible'}
          transition={{ duration: 0.5 }}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {renderSmallCard(item)}
        </motion.div>
        <motion.div
          variants={variants2}
          animate={state.currentKey === key ? 'hidden' : 'visible'}
          transition={{ duration: 0.5 }}
          style={{
            position: 'absolute',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            width: '100%',
            height: '100%',
            top: 0,
            //top: '-25%',
            //left: '-25%',
          }}
          onMouseOver={() => handleClick(key)}
          onMouseOut={() => handleClick()}
          // Stop the back-face click from bubbling to the card's navigation.
          onClick={e => {
            e.stopPropagation();
            handleClick(key);
          }}
        >
          {renderDetailedCard(item)}
        </motion.div>
      </Box>
    );
  };

  const renderCategory = item => {
    return (
      <Stack w="full" spacing={3}>
        <HStack w="full">
          <PageHeaderWithIcon
            label={item.key}
            icon={getIcon(
              state.groupBy?.value === 'cloud_provider'
                ? item.key
                : item.iconType,
            )}
            fontSize="lg"
            iconSize="sm"
            fontWeight={600}
            reversed
            border
            iconBgColor="white"
            useCustomColor
            iconColor="primary"
          />
          <Box color={customTheme.colors.gray[300]}>
            [{formatNumberWithComma(item.total_entities)}{' '}
            {item.total_entities === 1 ? 'entity' : 'entities'}]
          </Box>
        </HStack>
        <Divider borderColor={customTheme.colors.gray[100]} w="full" />
        <Wrap spacing={3} pt={1}>
          {map(item.resources, (o, index) => (
            <WrapItem key={index + '-category-count-inventory-overview'}>
              <Center>{renderCategoryCount(o)}</Center>
            </WrapItem>
          ))}
        </Wrap>
      </Stack>
    );
  };

  return (
    <Stack h="full" spacing={5}>
      <BorderedBox flex={1} w="full">
        <HStack spacing={3} justify="space-between">
          <HStack spacing={5}>
            <HStack>
              <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
              <Box zIndex={900}>
                <Select
                  options={groupByOptions}
                  value={state.groupBy}
                  onChange={s => updateState({ groupBy: s })}
                  showIconInValueContainer
                  showClearIndicator={false}
                  styles={styles}
                  isAFilter
                />
              </Box>
            </HStack>
            <HStack>
              <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
              <Box zIndex={900}>
                <Select
                  options={cloudProviderOptions}
                  value={state.cloud}
                  onChange={s => {
                    let filteredResourceTypeOptions: Record<string, any>[] =
                      state.resourceTypeOptions;
                    if (!!s?.value)
                      filteredResourceTypeOptions = state.resourceTypeOptions?.filter(
                        (o: Record<string, any>) =>
                          o?.data?.provider?.toLowerCase() ===
                          s.value?.toLowerCase(),
                      );
                    updateState({ cloud: s, filteredResourceTypeOptions });
                  }}
                  showIconInValueContainer
                  styles={styles}
                  isAFilter
                  leftMessage="Cloud"
                  showUndefinedValueLabel={false}
                />
              </Box>
              <RegionAndVPCFilter />
              <Box zIndex={900}>
                <Select
                  options={state.filteredResourceTypeOptions}
                  value={state.resourceType}
                  onChange={s => updateState({ resourceType: s })}
                  showIconInValueContainer
                  styles={styles}
                  isAFilter
                  leftMessage="Resource type"
                  showUndefinedValueLabel={false}
                />
              </Box>
            </HStack>
            <HStack>
              <FilterLabelWrapper>SORT BY</FilterLabelWrapper>
              <RadioButtonGroup
                options={sortByOptions}
                onChange={val =>
                  updateState({
                    sortBy:
                      state.sortBy === val?.value ? undefined : val?.value,
                  })
                }
                value={state.sortBy}
                styles={{ root: { direction: 'row', w: 'full' } }}
              />
            </HStack>
          </HStack>
          <Box>
            <OutlinedMenuButton
              menuItems={[
                {
                  label: 'Show Summary',
                  icon: <PieChartIcon />,
                  onClick: () => setShowInventorySummary(true),
                },
                {
                  label: 'Export to SIEM',
                  icon: <ExportIcon />,
                  onClick: () => exportAlerts.onOpen(),
                },
              ]}
            >
              Actions
            </OutlinedMenuButton>
          </Box>
        </HStack>
      </BorderedBox>
      {/*<ExportAlerts
          type={DataTypeForES.Resources}
          tooltip="Export inventory"
        />
        <PrimaryIconButton
          aria-label="summary"
          tooltip="Show Cloud Summary"
          onClick={() => setShowInventorySummary(true)}
        >
          <PieChartIcon />
        </PrimaryIconButton>*/}
      <Box w="full" h="full" overflow="auto">
        {inventoryOverview.isLoading ? (
          <Center w="full" h="full">
            <CustomSpinner size="xl" />
          </Center>
        ) : (
          <Stack spacing={5}>
            {map(state.categories, (o, index) => (
              <Box key={index + '-inventory-overview-category'}>
                <DashboardOverviewCard
                  key={index + 'inventory-overview-category'}
                  content={<Box w="full">{renderCategory(o)}</Box>}
                  showHeader={false}
                  styles={{
                    outerWrapper: {
                      _hover: {
                        borderColor: colorOpacity(
                          customTheme.colors.primary,
                          0.3,
                        ),
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                      },
                      //py: 0,
                      //px: 0
                      pb: 6,
                    },
                  }}
                />
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      {exportAlerts.isOpen && (
        <ExportAlertsModal
          type={DataTypeForES.Resources}
          isOpen={exportAlerts.isOpen}
          onClose={exportAlerts.onClose}
        />
      )}
    </Stack>
  );
};
